'use client';

import type { ReactNode } from 'react';

import type { SetupMacroStepDefinition } from '@/components/setup/experience/constants';
import { SetupProgressStrip } from '@/components/setup/experience/SetupProgressStrip';
import { SetupShellProvider } from '@/components/setup/experience/SetupShellContext';
import type { SetupExperienceStage } from '@/components/setup/experience/lib/setupSteps';
import { cn } from '@/lib/utils';

interface SetupShellProps {
  stage: SetupExperienceStage;
  macroIndex: number;
  /** Strip steps for the current store kind (4 physical, 3 digital). */
  macroSteps: SetupMacroStepDefinition[];
  /** Top-right escape hatch (e.g. Skip). Rendered on every stage except `complete`. */
  headerEnd?: ReactNode;
  children: ReactNode;
}

export function SetupShell({ stage, macroIndex, macroSteps, headerEnd, children }: SetupShellProps) {
  const isComplete = stage === 'complete';
  // The first step (`store`) is full-screen with no progress strip — it decides the flow's shape,
  // so the stepper only appears once it's behind us.
  const hideProgress = stage === 'store';

  if (isComplete) {
    return (
      <div
        className={cn(
          'bg-background text-foreground flex min-h-0 w-full flex-1 flex-col overflow-hidden',
          'flex min-h-0 flex-1 overflow-auto',
        )}
      >
        <div className="mx-auto flex min-h-0 min-h-[min(100%,100dvh)] w-full max-w-[90rem] flex-1 items-start justify-center overflow-y-auto overflow-x-hidden px-6 pb-10 pt-6 [overscroll-behavior:contain] [scroll-padding-bottom:2.5rem] [scrollbar-width:thin]">
          {children}
        </div>
      </div>
    );
  }

  return (
    <SetupShellProvider macroIndex={macroIndex} totalSteps={macroSteps.length} stage={stage}>
      <div className="bg-background text-foreground flex min-h-0 w-full flex-1 flex-col overflow-hidden">
        <div className="mx-auto flex min-h-0 w-full max-w-[80rem] flex-1 flex-col overflow-hidden px-6 pb-8 pt-6 md:py-10">
          {headerEnd ? (
            <div className="mb-4 flex shrink-0 items-center justify-end md:mb-5">{headerEnd}</div>
          ) : null}
          {hideProgress ? null : <SetupProgressStrip steps={macroSteps} currentIndex={macroIndex} />}

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden [overscroll-behavior:contain] [scroll-padding-bottom:2.5rem] [scrollbar-width:thin]">
                <div className="w-full duration-300 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none">
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SetupShellProvider>
  );
}
